import api from './api';

const adminService = {
  /**
   * Get all users with pagination and filtering
   */
  getUsers: async (params = {}) => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  /**
   * Get user details with subscription
   */
  getUserDetails: async (userId) => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },

  /**
   * Update user details
   */
  updateUser: async (userId, data) => {
    const response = await api.patch(`/admin/users/${userId}`, data);
    return response.data;
  },

  /**
   * Create new user manually
   */
  createUser: async (data) => {
    const response = await api.post('/admin/users', data);
    return response.data;
  },

  /**
   * Delete user
   */
  deleteUser: async (userId) => {
    await api.delete(`/admin/users/${userId}`);
  },

  /**
   * Get all subscriptions
   */
  getSubscriptions: async (params = {}) => {
    const response = await api.get('/admin/subscriptions', { params });
    return response.data;
  },

  /**
   * Update subscription
   */
  updateSubscription: async (subscriptionId, data) => {
    const response = await api.patch(`/admin/subscriptions/${subscriptionId}`, data);
    return response.data;
  },

  /**
   * Manually expire a subscription (for testing)
   */
  expireSubscription: async (subscriptionId) => {
    const response = await api.post(`/admin/subscriptions/${subscriptionId}/expire`);
    return response.data;
  },

  /**
   * Get platform statistics
   */
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  /**
   * Get admin settings (e.g., consent configuration)
   */
  getSettings: async () => {
    const response = await api.get('/admin/settings');
    return response.data;
  },

  /**
   * Update admin settings
   */
  updateSettings: async (data) => {
    const response = await api.patch('/admin/settings', data);
    return response.data;
  },

  // ==========================================
  // WhatsApp Management
  // ==========================================

  /**
   * Get WhatsApp server connection status
   */
  getWhatsAppStatus: async () => {
    const response = await api.get('/admin/whatsapp/status');
    return response.data;
  },

  /**
   * Get all WhatsApp chats
   */
  getWhatsAppChats: async (params = {}) => {
    const response = await api.get('/admin/whatsapp/chats', { params });
    return response.data;
  },

  /**
   * Get messages for a specific chat
   */
  getWhatsAppMessages: async (chatId, limit = 50) => {
    const response = await api.get(`/admin/whatsapp/chats/${encodeURIComponent(chatId)}/messages`, {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Send a WhatsApp message
   */
  sendWhatsAppMessage: async (phone, message) => {
    const response = await api.post('/admin/whatsapp/send', { phone, message });
    return response.data;
  },

  /**
   * Send bulk WhatsApp messages
   */
  sendBulkWhatsAppMessages: async (phones, message) => {
    const response = await api.post('/admin/whatsapp/send-bulk', { phones, message });
    return response.data;
  },

  /**
   * Get WhatsApp statistics
   */
  getWhatsAppStats: async () => {
    const response = await api.get('/admin/whatsapp/stats');
    return response.data;
  },

  /**
   * Get WhatsApp API configuration
   */
  getWhatsAppConfig: async () => {
    const response = await api.get('/admin/whatsapp/config');
    return response.data;
  },

  /**
   * Update WhatsApp API configuration
   */
  updateWhatsAppConfig: async (data) => {
    const response = await api.patch('/admin/whatsapp/config', data);
    return response.data;
  },

  // ==========================================
  // Institution Management (Super Admin Only)
  // ==========================================

  /**
   * Get all institutions
   */
  getInstitutions: async (params = {}) => {
    const response = await api.get('/institutions/', { params });
    return response.data;
  },

  /**
   * Create a new institution
   */
  createInstitution: async (data) => {
    const response = await api.post('/institutions/', data);
    return response.data;
  },

  /**
   * Delete an institution
   */
  deleteInstitution: async (institutionId) => {
    await api.delete(`/institutions/${institutionId}`);
  },

  // ==========================================
  // Bulk Coupon Management
  // ==========================================

  /**
   * Create bulk coupons for an institution
   */
  createBulkCoupons: async (data) => {
    const response = await api.post('/coupons/bulk', data);
    return response.data;
  },

  /**
   * Get coupons for a specific institution
   */
  getInstitutionCoupons: async (institutionId, params = {}) => {
    const response = await api.get(`/coupons/institution/${institutionId}`, { params });
    return response.data;
  },

  // ==========================================
  // Analytics & Campaign Dashboard
  // ==========================================

  // Get analytics dashboard data (stats, funnel, health)
  getAnalyticsDashboard: async () => {
    const response = await api.get('/admin/analytics/dashboard');
    return response.data;
  },

  // Get paginated campaign history
  getCampaignHistory: async (params = {}) => {
    const response = await api.get('/admin/analytics/campaigns', { params });
    return response.data;
  },

  // Get analytics profile for a specific user
  getUserAnalytics: async (userId) => {
    const response = await api.get(`/admin/analytics/user/${userId}`);
    return response.data;
  },

  // Manually trigger campaign automation
  runCampaigns: async () => {
    const response = await api.post('/admin/analytics/run-campaigns');
    return response.data;
  },
};

export default adminService;
